import React, { useState, useEffect } from 'react';
import { Modal } from '@common/Modal';
import { Button } from '@common/Button';
import { RefreshCw, Check, AlertTriangle, Clock } from 'lucide-react';
import { updateAllLayersFromGeoJSON, getAvailableLayers } from '../../../../utils/wfs/importLayersFromGeoJSON';
import { logError, logInfo } from '@utils/logger';
import { formatDistanceToNow } from 'date-fns';
import { fr } from 'date-fns/locale';

export const LayerUpdateModal = ({ isOpen, onClose }) => {
  const [updating, setUpdating] = useState(false);
  const [layers, setLayers] = useState([]);
  const [updateResults, setUpdateResults] = useState(null);
  const [error, setError] = useState(null);

  // Load layer status when modal opens
  useEffect(() => {
    if (isOpen) {
      loadLayerStatus();
    }
  }, [isOpen]);

  const loadLayerStatus = async () => {
    try {
      const layersStatus = await getAvailableLayers();
      setLayers(layersStatus);
    } catch (error) {
      logError('Failed to load layer status:', error);
      setError('Impossible de charger le statut des couches');
    }
  };

  const handleUpdateLayers = async () => {
    setUpdating(true);
    setError(null);
    setUpdateResults(null);

    try {
      const results = await updateAllLayersFromGeoJSON();
      setUpdateResults(results);
      
      // Reload layer status
      await loadLayerStatus();
      
      logInfo('Layers updated successfully', results);
    } catch (error) {
      logError('Failed to update layers:', error);
      setError('Échec de la mise à jour des couches');
    } finally {
      setUpdating(false);
    }
  };

  const formatTimestamp = (timestamp) => {
    if (!timestamp) return 'Jamais';
    
    try {
      const date = new Date(timestamp);
      return formatDistanceToNow(date, { addSuffix: true, locale: fr });
    } catch (error) {
      return timestamp;
    }
  };

  return (
    <Modal isOpen={isOpen} onClose={onClose} title="Mise à jour des couches">
      <div className="p-6 space-y-6">
        <div className="bg-blue-500/10 text-blue-400 p-4 rounded-lg mb-4">
          <h3 className="font-medium mb-2">À propos des couches de données</h3>
          <p className="text-sm">
            Les couches de données sont téléchargées depuis les sources officielles du Ministère des Transports et de la Mobilité durable du Québec et stockées localement pour améliorer les performances.
          </p>
        </div>

        {/* Layer Status */}
        <div className="space-y-4">
          <div className="flex items-center justify-between">
            <h3 className="text-white font-medium">Statut des couches</h3>
            <Button
              onClick={loadLayerStatus}
              className="p-2 bg-gray-700 hover:bg-gray-600 text-white rounded"
              title="Rafraîchir le statut"
            >
              <RefreshCw className="w-4 h-4" />
            </Button>
          </div>

          <div className="bg-gray-700/50 rounded-lg overflow-hidden">
            <table className="w-full text-sm">
              <thead className="bg-gray-800/50">
                <tr>
                  <th className="text-left py-2 px-4 text-gray-300">Couche</th>
                  <th className="text-left py-2 px-4 text-gray-300">Dernière mise à jour</th>
                  <th className="text-left py-2 px-4 text-gray-300">Statut</th>
                </tr>
              </thead>
              <tbody>
                {layers.map((layer) => (
                  <tr key={layer.id} className="border-t border-gray-700">
                    <td className="py-2 px-4 text-white">{layer.name}</td>
                    <td className="py-2 px-4 text-gray-300 whitespace-nowrap">
                      <div className="flex items-center gap-2">
                        <Clock className="w-4 h-4 text-gray-400" />
                        {formatTimestamp(layer.updatedAt)}
                      </div>
                    </td>
                    <td className="py-2 px-4">
                      {layer.isAvailable ? (
                        <span className="inline-flex items-center gap-1 text-green-400">
                          <Check className="w-4 h-4" />
                          Disponible
                        </span>
                      ) : (
                        <span className="inline-flex items-center gap-1 text-yellow-400">
                          <AlertTriangle className="w-4 h-4" /> 
                          {layer.featureCount ? `${layer.featureCount} objets` : 'Non disponible'}
                        </span>
                      )}
                    </td>
                  </tr>
                ))}
                {layers.length === 0 && (
                  <tr>
                    <td colSpan="3" className="py-4 text-center text-gray-400">
                      Aucune information disponible
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </div>

        {/* Update Results */}
        {updateResults && (
          <div className="space-y-4">
            <h3 className="text-white font-medium">Résultats de la mise à jour</h3>
            
            {updateResults.success.length > 0 && (
              <div className="bg-green-500/10 text-green-400 p-4 rounded-lg">
                <h4 className="font-medium mb-2">Couches mises à jour avec succès ({updateResults.success.length})</h4>
                <ul className="list-disc list-inside space-y-1">
                  {updateResults.success.map((result) => (
                    <li key={result.layerId}>
                      {result.name} ({result.featuresCount} objets)
                    </li>
                  ))}
                </ul>
              </div>
            )}
            
            {updateResults.failed.length > 0 && (
              <div className="bg-red-500/10 text-red-400 p-4 rounded-lg">
                <h4 className="font-medium mb-2">Échecs ({updateResults.failed.length})</h4>
                <ul className="list-disc list-inside space-y-1">
                  {updateResults.failed.map((result) => (
                    <li key={result.layerId}>
                      {result.name}: {result.error}
                    </li>
                  ))}
                </ul>
              </div>
            )}
          </div>
        )}

        {/* Error Message */}
        {error && (
          <div className="bg-red-500/10 text-red-400 p-4 rounded-lg">
            {error}
          </div>
        )}

        {/* Action Buttons */}
        <div className="flex justify-end gap-3">
          <Button
            onClick={onClose}
            className="bg-gray-700 hover:bg-gray-600 text-white"
          >
            Fermer
          </Button>
          <Button
            onClick={handleUpdateLayers}
            loading={updating}
            className="bg-blue-600 hover:bg-blue-700 text-white"
          >
            {updating ? 'Mise à jour en cours...' : 'Mettre à jour les couches'}
          </Button>
        </div>
      </div>
    </Modal>
  );
};